/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
/** biome-ignore-all lint/complexity/noExcessiveLinesPerFunction: too many lines */
import {useAutoAnimate} from '@formkit/auto-animate/react';
import cx from 'classnames';
import {useCallback, useMemo, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {ConfirmDeleteSlotsModal} from '~/SignUpSheets/Create/routes/Customize/components/Builder/Elements/ConfirmDeleteSlotsModal';
import {Sidebar} from '~/SignUpSheets/Create/routes/Customize/components/Builder/Elements/Fields/Sidebar';
import {SignupOptionTools} from '~/SignUpSheets/Create/routes/Customize/components/Builder/Elements/SlotToolBar/SignupOptionTools';
import {AddButtonWithTitle} from '~/SignUpSheets/components/AddButtonWithTitle/AddButtonWithTitle';
import {DetailsToggle} from '~/SignUpSheets/components/DetailsToggle/DetailsToggle';
import {FilledSlots} from '~/SignUpSheets/components/FilledSlots/FilledSlots';
import {RemoveActiveSlotModal} from '~/SignUpSheets/components/LeaveModal/LeaveModal';
import {ProductLinkInfoButton} from '~/SignUpSheets/components/ProductLinkInfoButton/ProductLinkInfoButton';
import {useAffiliateActions} from '~/SignUpSheets/hooks/useAffiliateActions';
import {useUnlockGate} from '~/SignUpSheets/hooks/useUnlockGate';
import {BLUEPRINT_TYPE_ITEMS} from '~/SignUpSheets/reducers/blueprint/constants';
import {
  selectBlueprintType,
  selectFieldSchema,
  selectIsDateTimeBlueprint,
} from '~/SignUpSheets/reducers/blueprint/selectors';
import {SCOPE_HOST} from '~/SignUpSheets/reducers/sheet/constants';
import {
  selectErrors,
  selectGateUnlockedForSheet,
  selectIsFirstSignupOptionInGroup,
  selectIsInEditMode,
  selectIsLastSignupOption,
  selectIsLastSignupOptionInGroup,
  selectIsOnlySignupOption,
  selectIsOrganizer,
  selectIsSheetExpiredOrCanceledOrArchived,
  selectSignupOptionById,
  selectSignupsByUuid,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {selectSlotColor} from '~/SignUpSheets/reducers/themes/selectors';
import {entryHasProductLink} from '~/SignUpSheets/utils/misc';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp, useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {GatedContent} from '~library/molecules/GatedContent';
import {DateField} from './Fields/DateField';
import {LinkField} from './Fields/LinkField';
import {NumberField} from './Fields/NumberField';
import {SlotsField} from './Fields/SlotsField';
import {TextField} from './Fields/TextField';
import {TimesField} from './Fields/TimesField';
import {MobileSignupOptionTools} from './SlotToolBar/mobile/MobileSignupOptionTools';

import './Elements.sass';

const ITEM_MAP = Object.freeze({
  text: TextField,
  number: NumberField,
  date: DateField,
  slots: SlotsField,
  link: LinkField,
  times: TimesField,
});

export const ElementRenderer = ({
  schema: s,
  fieldIndex,
  rowIndex,
  values,
  errors = [],
  hasSignups = false,
  grouped = false,
  handleEdit,
  processProductLinks,
  disabled = false,
  linkPreviewsForRow = {},
  getOnDelete,
  numSignedUpSlots = 0,
  userId,
  blueprintType,
  isInEditMode = false,
  dataQaId = `slot-item-${s.name}-${rowIndex}`,
  expiredOrCanceled = false,
}) => {
  const isDesktop = useIsLargishAndUp();

  // When leaving the slot title field (i.e. "item" or "date"),
  // if this is a new signup option where quantity has not yet been touched,
  // autofocus to it
  const highlightQuantity = useCallback(() => {
    const quantityField = document.querySelector(
      grouped
        ? `[data-qa-id=slot-group-${values.group}-item-quantity-${rowIndex}]`
        : `[data-qa-id=slot-item-quantity-${rowIndex}]`
    );
    if (quantityField && values.pristine_quantity) quantityField.focus();
  }, [rowIndex, values.pristine_quantity, grouped, values.group]);

  if (s.type === 'link') {
    return (
      <LinkField
        key={s.name}
        uuid={values.uuid}
        newOption={values.new}
        copied={values.copied}
        schema={s}
        value={values[s.name]}
        className={cx(`builder__item-row__type-${s.name}`, {'has-signups': hasSignups, grouped})}
        onChange={(value, saveChanges = true) =>
          handleEdit(values.uuid, {[s.name]: value}, values.position_index, saveChanges)
        }
        onBlur={() => processProductLinks()}
        errors={errors.filter(
          (e) =>
            e.loc?.[0] === 'signup_options' &&
            e.loc?.[1] === values?.position_index &&
            e.loc?.[2] === s.name
        )}
        disabled={disabled}
        preview={linkPreviewsForRow[values.uuid]}
        data-qa-id={dataQaId}
        hasSignups={hasSignups}
        isDesktop={isDesktop}
      />
    );
  }
  if (s.type === 'times') {
    return (
      <TimesField
        key={s.name}
        id={values.uuid}
        dataQaId={dataQaId}
        startTime={values.start_time}
        endTime={values.end_time}
        defaultStart={s.placeholder[0]}
        defaultEnd={s.placeholder[1]}
        className={cx(`builder__item-row__type-${s.name}`, {'has-signups': hasSignups, grouped})}
        onChangeStartTime={(value, saveChanges = true) =>
          handleEdit(values.uuid, {start_time: value}, values.position_index, saveChanges)
        }
        onChangeEndTime={(value, saveChanges = true) =>
          handleEdit(values.uuid, {end_time: value}, values.position_index, saveChanges)
        }
        startError={errors.filter(
          (e) =>
            e.loc?.[0] === 'signup_options' &&
            e.loc?.[1] === values?.position_index &&
            e.loc?.[2] === 'start_time'
        )}
        endError={errors.filter(
          (e) =>
            e.loc?.[0] === 'signup_options' &&
            e.loc?.[1] === values?.position_index &&
            e.loc?.[2] === 'end_time'
        )}
        disabled={disabled}
      />
    );
  }
  const Element = ITEM_MAP[s.type];
  return (
    <Element
      key={s.name}
      uuid={values.uuid}
      schema={s}
      pristine={s.name === 'quantity' && values.pristine_quantity}
      signupOption={values}
      value={values[s.name]}
      currentUserId={userId}
      allDay={values.all_day}
      copied={values.copied}
      isDesktop={isDesktop}
      className={cx(`builder__item-row__type-${s.name}`, {
        'has-signups': hasSignups,
        'builder__item-row--expired': expiredOrCanceled,
        grouped,
      })}
      onChange={(value, saveChanges = true) =>
        handleEdit(values.uuid, {[s.name]: value}, values.position_index, saveChanges)
      }
      onBlur={['item', 'date'].includes(s.name) ? highlightQuantity : null}
      onHandleProductLinkBlur={(sos) => processProductLinks(sos)}
      index={fieldIndex}
      onDelete={getOnDelete(s)}
      min={Math.max(1, numSignedUpSlots)}
      errors={errors.filter(
        (e) =>
          e.loc?.[0] === 'signup_options' &&
          e.loc?.[1] === values?.position_index &&
          e.loc?.[2] === s.name
      )}
      disabled={disabled}
      data-qa-id={dataQaId}
      isInEditMode={isInEditMode}
      newOption={values.new}
      blueprintType={blueprintType}
      hasSignups={hasSignups}
      linkPreviews={linkPreviewsForRow}
      required={
        s.required || (s.required_if_not_grouped && !grouped) || (s.required_if_grouped && grouped)
      }
    />
  );
};

export const Elements = ({
  id,
  index,
  onEdit,
  onDeleteItem,
  onDuplicateItem,
  onMoveItemUp,
  onMoveItemDown,
  onCopySlotsToEmptyDate,
  grouped = false,
  expanded = false,
  onToggle,
}) => {
  const values = useSelector(selectSignupOptionById(id), shallowEqual);
  const [removeCallback, setRemoveCallback] = useState(null);
  const [showConfirmDeletion, setShowConfirmDeletion] = useState(false);
  const blueprintType = useSelector(selectBlueprintType);
  const isDatetimeType = useSelector(selectIsDateTimeBlueprint);
  const isFirstInGroup = useSelector(
    selectIsFirstSignupOptionInGroup({uuid: id, groupId: values.group})
  );
  const canMoveUp = !isDatetimeType && ((!grouped && index > 0) || (grouped && !isFirstInGroup));
  const isLastSignupOption = useSelector(selectIsLastSignupOption(index));
  const isLastInGroup = useSelector(
    selectIsLastSignupOptionInGroup({uuid: id, groupId: values.group})
  );
  const isOrganizer = useSelector(selectIsOrganizer);
  const canMoveDown = !isDatetimeType && !isLastSignupOption && !isLastInGroup;
  const isOnlySignupOption = useSelector(selectIsOnlySignupOption);
  const schema = useSelector(selectFieldSchema, shallowEqual);
  const userId = useSelector((state) => state.user?.userId);
  const fieldSchema = useMemo(() => schema.filter((s) => s.scope === SCOPE_HOST), [schema]);
  const errors = useSelector(selectErrors, shallowEqual);
  const isInEditMode = useSelector(selectIsInEditMode);
  const signups = useSelector(selectSignupsByUuid(values.uuid), shallowEqual);
  const hasSignups = signups.length > 0;
  const expiredOrCanceled = useSelector((s) =>
    selectIsSheetExpiredOrCanceledOrArchived(s, {uuid: values.uuid})
  );
  const slotColor = useSelector(selectSlotColor);
  const isMedium = useIsMediumAndUp();
  const isDesktop = useIsLargishAndUp();
  const [showDescription, setShowDescription] = useState(values.description ?? isDesktop);
  const [showProductLink, setShowProductLink] = useState(entryHasProductLink(values) ?? isDesktop);
  const [rowAnimation] = useAutoAnimate({
    duration: 250,
    easing: 'ease-in-out',
  });
  const {processProductLinks, linkPreviews} = useAffiliateActions();

  const autofocusAdditionalDetails = useCallback(
    (fieldName = 'description') => {
      if (evite.detects.isMobile) return;

      // If document.activeElement is document.body or the "Add details" DetailsToggle,
      // allow to proceed, otherwise, some other element is already active, so do not continue.
      const PASSABLE_ACTIVE_ELEMENTS = [document.body];
      const addDetailsButton = document.querySelector(
        `[data-qa-id=builder-item-row-${index}-add-details]`
      );
      if (addDetailsButton) PASSABLE_ACTIVE_ELEMENTS.push(addDetailsButton);
      if (!PASSABLE_ACTIVE_ELEMENTS.includes(document.activeElement)) return;

      if (fieldName === 'product_link') {
        // Autofocus to product link if it's empty
        const field = document.querySelector(`[data-qa-id=slot-item-product_link-${index}-url]`);
        if (field && !field.value) field.focus();
      } else if (fieldName === 'description') {
        // Autofocus to description if it's empty
        const field = document.querySelector(`[data-qa-id=slot-item-description-${index}]`);
        if (field && !field.value) field.focus();
      }
    },
    [index]
  );

  const revealDescriptionField = useCallback(
    (autofocus = true) => {
      setShowDescription(true);
      if (!autofocus) return;

      // Autofocus to description field if it's empty
      setTimeout(() => {
        autofocusAdditionalDetails('description');
      }, 300);
    },
    [autofocusAdditionalDetails]
  );

  const revealProductLinkField = useCallback(
    (autofocus = true) => {
      setShowProductLink(true);
      if (!autofocus) return;

      // Autofocus to product link field if it's empty
      setTimeout(() => {
        autofocusAdditionalDetails('product_link');
      }, 300);
    },
    [autofocusAdditionalDetails]
  );

  const handleEdit = useCallback(
    (uuid, payload, pos = -1, saveChanges = true) => {
      if ('description' in payload && payload.description) {
        revealDescriptionField(false);
      }
      onEdit(uuid, payload, pos, saveChanges);
    },
    [revealDescriptionField, onEdit]
  );

  const handleOnDeleteItem = useCallback(
    (idx) => {
      if (isDatetimeType && hasSignups) {
        setRemoveCallback(() => () => {
          onDeleteItem(idx, signups);
          setRemoveCallback(null);
        });
      } else {
        setShowConfirmDeletion(true);
      }
    },
    [isDatetimeType, hasSignups, onDeleteItem, signups]
  );

  const getOnDelete = useCallback(
    (s) => {
      if (s.name === 'description' && showDescription) {
        return () => {
          handleEdit(values.uuid, {description: ''}, values.position_index, false);
          setShowDescription(false);
        };
      }
      return null;
    },
    [handleEdit, showDescription, values]
  );

  const linkPreviewsForRow = useMemo(() => {
    if (!isDatetimeType || !values.slots) {
      return {[values.uuid]: linkPreviews[values.uuid]};
    }

    return values.slots.reduce((acc, slot) => {
      acc[slot.slot_id] = linkPreviews[slot.slot_id];
      return acc;
    }, {});
  }, [isDatetimeType, values.slots, values.uuid, linkPreviews]);

  const gateUnlocked = useSelector(selectGateUnlockedForSheet);
  const gateThisSlot = hasSignups && isInEditMode && !gateUnlocked;

  const inputs = useMemo(() => {
    const record = {
      default: {},
      hidden_if_grouped: {},
    };
    fieldSchema.forEach((s, i) => {
      const disabled =
        (isInEditMode &&
          (expiredOrCanceled || (s.disabled_in_edit_mode && !values.new && hasSignups))) ||
        gateThisSlot;

      if (!Object.keys(ITEM_MAP).includes(s.type)) {
        record.default[s.name] = null;
        record.hidden_if_grouped[s.name] = null;
      } else if (grouped && s.collapsed_if_grouped) {
        record.default[s.name] = null;
        record.hidden_if_grouped[s.name] = (
          <ElementRenderer
            key={s.name}
            schema={s}
            rowIndex={index}
            fieldIndex={i}
            values={values}
            hasSignups={hasSignups}
            grouped={grouped}
            handleEdit={handleEdit}
            processProductLinks={processProductLinks}
            disabled={disabled}
            linkPreviewsForRow={linkPreviewsForRow}
            getOnDelete={getOnDelete}
            numSignedUpSlots={signups.length}
            userId={userId}
            isInEditMode={isInEditMode}
            blueprintType={blueprintType}
            errors={errors}
            dataQaId={`slot-${grouped ? `group-${values.group}-` : ''}item-${s.name}-${index}`}
          />
        );
      } else {
        record.hidden_if_grouped[s.name] = null;
        const isDescription = s.name === 'description';
        const isProductLink = s.name === 'product_link';
        if (
          isDescription &&
          (blueprintType === BLUEPRINT_TYPE_ITEMS || !isDesktop) &&
          !disabled &&
          !showDescription
        ) {
          record.default[s.name] = (
            <div className="builder__item-row__add-description" key={s.name}>
              <AddButtonWithTitle
                dataQaId={`add-description-${index}`}
                onClick={revealDescriptionField}
                label="Add additional details"
              />
            </div>
          );
        } else if (isProductLink && !isDesktop && !disabled && !showProductLink) {
          const popoverButtonAvailable = s.label_adornment && isOrganizer;
          record.default[s.name] = (
            <div className="builder__item-row__add-link" key={s.name}>
              <AddButtonWithTitle
                dataQaId={`add-productlink-${index}`}
                onClick={revealProductLinkField}
                label="Add link"
              />
              {popoverButtonAvailable ? (
                <ProductLinkInfoButton
                  dataQaId={`slot-item-${s.name}-${index}`}
                  uuid={values.uuid}
                />
              ) : null}
            </div>
          );
        } else {
          record.default[s.name] = (
            <ElementRenderer
              key={s.name}
              schema={s}
              rowIndex={index}
              fieldIndex={i}
              values={values}
              hasSignups={hasSignups}
              grouped={grouped}
              handleEdit={handleEdit}
              processProductLinks={processProductLinks}
              disabled={disabled}
              linkPreviewsForRow={linkPreviewsForRow}
              getOnDelete={getOnDelete}
              numSignedUpSlots={signups.length}
              userId={userId}
              isInEditMode={isInEditMode}
              blueprintType={blueprintType}
              errors={errors}
              dataQaId={`slot-${grouped ? `group-${values.group}-` : ''}item-${s.name}-${index}`}
              expiredOrCanceled={expiredOrCanceled}
            />
          );
        }
      }
    });
    return record;
  }, [
    fieldSchema,
    values,
    userId,
    showDescription,
    showProductLink,
    isDesktop,
    revealDescriptionField,
    isInEditMode,
    handleEdit,
    errors,
    getOnDelete,
    signups.length,
    hasSignups,
    blueprintType,
    linkPreviewsForRow,
    grouped,
    index,
    gateThisSlot,
    isOrganizer,
    expiredOrCanceled,
    processProductLinks,
    revealProductLinkField,
  ]);

  const SignupOptionToolsElement = useMemo(
    () => (
      <SignupOptionTools
        index={index}
        isMobile={!isMedium}
        isOnlySignupOption={isOnlySignupOption}
        blueprintType={blueprintType}
        onDeleteItem={handleOnDeleteItem}
        onMoveItemUp={onMoveItemUp}
        onMoveItemDown={onMoveItemDown}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        onDuplicateItem={onDuplicateItem}
        onCopySlotsToEmptyDate={onCopySlotsToEmptyDate}
        isDesktop={isDesktop}
        as={isDatetimeType && !isDesktop ? 'menu' : 'buttons'}
      />
    ),
    [
      index,
      isMedium,
      isDesktop,
      isOnlySignupOption,
      blueprintType,
      handleOnDeleteItem,
      onMoveItemUp,
      onMoveItemDown,
      canMoveUp,
      canMoveDown,
      onDuplicateItem,
      onCopySlotsToEmptyDate,
      isDatetimeType,
    ]
  );

  const {handleUnlock} = useUnlockGate();
  return (
    <div
      className={cx(`builder__row builder__item-row-${values.uuid}`, {grouped})}
      data-qa-id={`builder-item-${index}`}
      id={`signup-option-${values.uuid}`}
    >
      <GatedContent
        onClick={handleUnlock}
        heading="This slot already has volunteers. \nWould you like to make changes?"
        dataQaId={`edit-item-with-signups-${values.uuid}`}
        isGated={gateThisSlot && !isDatetimeType}
      >
        <div
          className={cx(
            'builder__item-row__wrapper-and-desktop-controls',
            `builder__type-${blueprintType}`,
            {grouped}
          )}
        >
          <div
            className={cx('builder__item-row-wrapper', `builder__type-${blueprintType}__wrapper`, {
              grouped,
            })}
            style={{
              backgroundColor: blueprintType === BLUEPRINT_TYPE_ITEMS ? slotColor : undefined,
            }}
          >
            <div
              className={cx(
                'builder__item-row__inputs-and-mw-controls',
                `builder__type-${blueprintType}`
              )}
            >
              <div
                className={cx('builder__item-row', `builder__type-${blueprintType}`, {
                  'has-signups': hasSignups,
                  grouped,
                })}
                ref={rowAnimation}
                data-qa-id={`builder-item-elements-${index}`}
              >
                {!isMedium && !grouped && !isDatetimeType && (
                  <MobileSignupOptionTools
                    index={index}
                    onDeleteItem={handleOnDeleteItem}
                    onDuplicateItem={onDuplicateItem}
                    onCopySlotsToEmptyDate={onCopySlotsToEmptyDate}
                    onMoveItemUp={onMoveItemUp}
                    onMoveItemDown={onMoveItemDown}
                    canMoveUp={canMoveUp}
                    canMoveDown={canMoveDown}
                    isOnlySignupOption={isOnlySignupOption}
                    blueprintType={blueprintType}
                  />
                )}
                <InputsView
                  inputs={inputs.default}
                  values={values}
                  onChange={(k, value) =>
                    handleEdit(values.uuid, {[k]: value}, values.position_index)
                  }
                  expanded={expanded}
                  onToggle={onToggle}
                  toolsMenu={
                    isDesktop || !isDatetimeType ? null : (
                      <MobileSignupOptionTools
                        index={index}
                        onDeleteItem={handleOnDeleteItem}
                        onDuplicateItem={onDuplicateItem}
                        onCopySlotsToEmptyDate={onCopySlotsToEmptyDate}
                        onMoveItemUp={onMoveItemUp}
                        onMoveItemDown={onMoveItemDown}
                        canMoveUp={canMoveUp}
                        canMoveDown={canMoveDown}
                        isOnlySignupOption={isOnlySignupOption}
                        blueprintType={blueprintType}
                        inline={!isDesktop}
                      />
                    )
                  }
                  hasSignups={hasSignups}
                />
              </div>
              {isMedium || !grouped || isDatetimeType ? null : SignupOptionToolsElement}
            </div>
            {grouped && (
              <DetailsToggle
                dataQaId={`builder-item-row-${index}-add-details`}
                defaultOpen={Object.entries(values).some(([k, v]) => {
                  const field = fieldSchema.find((s) => s.name === k);
                  return field?.collapsed_if_grouped && (k === 'product_link' ? v?.url : !!v);
                })}
                wrapperClassName="builder-item-row__grouped-additional-details__wrapper"
                contentClassName="builder-item-row__grouped-additional-details"
                autoFocusListener={autofocusAdditionalDetails}
              >
                {Object.values(inputs.hidden_if_grouped)}
              </DetailsToggle>
            )}
            {!isDatetimeType && (signups.length > 0 || expiredOrCanceled) && (
              <div className="builder__signups">
                <FilledSlots
                  slots={signups}
                  signedInId={userId}
                  isOrganizer
                  titleElement={
                    <div className="builder__signups-title">
                      <Typography variant="label2" size="smaller">
                        Confirmed Signups
                      </Typography>
                      {values.quantity > 0 && (
                        <Typography variant="list2" size="small" color="charcoal">
                          {signups.length} of {values.quantity} filled
                        </Typography>
                      )}
                    </div>
                  }
                />
              </div>
            )}
          </div>
          {(isMedium && !isDatetimeType) || isDesktop ? SignupOptionToolsElement : null}
        </div>
      </GatedContent>
      {hasSignups && removeCallback !== null && (
        <RemoveActiveSlotModal cancel={() => setRemoveCallback(null)} remove={removeCallback} />
      )}
      {showConfirmDeletion && (
        <ConfirmDeleteSlotsModal
          onSubmit={() => {
            onDeleteItem(index, signups);
            setShowConfirmDeletion(false);
          }}
          onClose={() => setShowConfirmDeletion(false)}
        />
      )}
    </div>
  );
};

const InputsView = ({
  inputs = [],
  values,
  onChange,
  expanded = false,
  onToggle,
  toolsMenu = null,
  hasSignups = false,
}) => {
  const sidebarInputs = Object.values(inputs).filter(
    (item) => item?.props?.schema?.in_sidebar === true
  );

  const slotInputs = Object.values(inputs).filter(
    (item) => item?.props?.schema?.in_sidebar !== true
  );

  const onDescriptionChange = useCallback(
    (newValue) => {
      onChange('description', newValue);
    },
    [onChange]
  );

  const onTitleChange = useCallback(
    (newValue) => {
      onChange('title', newValue);
    },
    [onChange]
  );

  const onToggleTimeSlots = useCallback(
    (newValue) => {
      onChange('all_day', !newValue);
    },
    [onChange]
  );

  const hasSidebar = sidebarInputs.length > 0;

  return hasSidebar ? (
    <>
      <Sidebar
        dayDescription={values.description}
        dateTitle={values.title}
        allDay={values.all_day}
        onDescriptionChange={onDescriptionChange}
        onTitleChange={onTitleChange}
        onToggleTimeSlots={onToggleTimeSlots}
        sidebarInputs={sidebarInputs}
        uuid={values.uuid}
        expanded={expanded}
        onToggle={onToggle}
        toolsMenu={toolsMenu}
        hasSignups={hasSignups}
      />
      {expanded ? slotInputs : null}
    </>
  ) : (
    Object.values(inputs)
  );
};
